import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { AuctionHallService } from 'src/app/services/auction-hall/auction-hall.service';
import { TransportPickupModalService } from 'src/app/services/transport-pickup-modal/transport-pickup-modal.service';
import { TransportPickupService } from 'src/app/services/transport-pickup/transport-pickup.service';
import { VehicleService } from 'src/app/services/vehicle/vehicle.service';
import { VendorService } from 'src/app/services/vendor/vendor.service';
import Swal from 'sweetalert2';

declare var bootstrap: any;

@Component({
  selector: 'app-transport-pickup-add',
  templateUrl: './transport-pickup-add.component.html',
  styleUrls: ['./transport-pickup-add.component.scss']
})
export class TransportPickupAddComponent extends DestructorComponent implements OnInit {

  @Output() transportPickupAdded = new EventEmitter<void>();
  @Output() transportPickupUpdated = new EventEmitter<void>();

  transportPickupForm: FormGroup;
  isLoading: boolean = false;
  isEditMode: boolean = false;
  selectedTransportPickup: any = null;
  isVehiclePreSelected: boolean = false;

  // Vehicle search properties
  vehicleSearchTerm: string = '';
  vehicleSearchResults: any[] = [];
  selectedVehicle: any = null;
  showVehicleDropdown: boolean = false;
  isVehicleSearching: boolean = false;
  private vehicleSearchSubject = new Subject<string>();

  // Vendor search properties
  vendorSearchTerm: string = '';
  vendorSearchResults: any[] = [];
  selectedVendor: any = null;
  showVendorDropdown: boolean = false;
  isVendorSearching: boolean = false;
  private vendorSearchSubject = new Subject<string>();

  // Pickup From search properties
  pickupFromSearchTerm: string = '';
  pickupFromSearchResults: any[] = [];
  selectedPickupFrom: any = null;
  showPickupFromDropdown: boolean = false;
  isPickupFromSearching: boolean = false;
  private pickupFromSearchSubject = new Subject<string>();

  // Delivered At search properties
  deliveredAtSearchTerm: string = '';
  deliveredAtSearchResults: any[] = [];
  selectedDeliveredAt: any = null;
  showDeliveredAtDropdown: boolean = false;
  isDeliveredAtSearching: boolean = false;
  private deliveredAtSearchSubject = new Subject<string>();

  constructor(
    private fb: FormBuilder,
    private _transportPickupService: TransportPickupService,
    private _transportPickupModalService: TransportPickupModalService,
    private _vehicleService: VehicleService,
    private _vendorService: VendorService,
    private _auctionHallService: AuctionHallService
  ) {
    super();
    this.initializeForm();
    this.setupVehicleSearchDebounce();
    this.setupVendorSearchDebounce();
    this.setupPickupFromSearchDebounce();
    this.setupDeliveredAtSearchDebounce();
  }

  ngOnInit(): void {
    this.setupModalListener();
  }

  private initializeForm(): void {
    this.transportPickupForm = this.fb.group({
      vehicle_id: [''],
      vehicle_name: [''],
      order_date: ['', [Validators.required]],
      yard_in: [''],
      out_date: [''],
      yard_out: [''],
      pos_no: [''],
      pickup_from: [''],
      pickup_from_id: [''],
      delivered_at: [''],
      delivered_at_id: [''],
      transport_company_id: [''],
      transport_company_name: [''],
      comment_for_transporter: [''],
      admin_comment: ['']
    });
  }

  private setupModalListener(): void {
    const modalElement = document.getElementById('kt_modal_transport_pickup_add_edit');
    if (modalElement) {
      modalElement.addEventListener('shown.bs.modal', () => {
        const serviceTransportPickup = this._transportPickupModalService.getSelectedTransportPickup();
        const preSelectedVehicle = this._transportPickupModalService.getPreSelectedVehicle();

        if (serviceTransportPickup) {
          this.isEditMode = true;
          this.isVehiclePreSelected = false;
          this.selectedTransportPickup = serviceTransportPickup;
          this.populateForm(this.selectedTransportPickup);
        } else if (preSelectedVehicle) {
          this.isEditMode = false;
          this.isVehiclePreSelected = true;
          this.selectedTransportPickup = null;
          this.resetForm();
          this.populateVehicleFromPreSelected(preSelectedVehicle);
        } else {
          this.isEditMode = false;
          this.isVehiclePreSelected = false;
          this.selectedTransportPickup = null;
          this.resetForm();
        }
      });

      modalElement.addEventListener('hidden.bs.modal', () => {
        this._transportPickupModalService.clearSelectedTransportPickup();
        this._transportPickupModalService.clearPreSelectedVehicle();
        this.resetForm();
      });
    }
  }

  private populateForm(transportPickup: any): void {
    if (transportPickup) {
      this.transportPickupForm.patchValue({
        vehicle_id: transportPickup.vehicle_id || '',
        vehicle_name: transportPickup.vehicle_name || '',
        order_date: transportPickup.order_date || '',
        yard_in: transportPickup.yard_in || '',
        out_date: transportPickup.out_date || '',
        yard_out: transportPickup.yard_out || '',
        pos_no: transportPickup.pos_no || '',
        pickup_from: transportPickup.pickup_from || '',
        pickup_from_id: transportPickup.pickup_from_id || '',
        delivered_at: transportPickup.delivered_at || '',
        delivered_at_id: transportPickup.delivered_at_id || '',
        transport_company_id: transportPickup.transport_company_id || '',
        transport_company_name: transportPickup.transport_company_name || '',
        comment_for_transporter: transportPickup.comment_for_transporter || '',
        admin_comment: transportPickup.admin_comment || ''
      });

      // Set vehicle search term and selected vehicle if available
      if (transportPickup.vehicle_name) {
        this.vehicleSearchTerm = transportPickup.vehicle_name;
        this.selectedVehicle = {
          _id: transportPickup.vehicle_id,
          name: transportPickup.vehicle_name
        };
      }

      // Set vendor search term and selected vendor if available
      if (transportPickup.transport_company_name) {
        this.vendorSearchTerm = transportPickup.transport_company_name;
        this.selectedVendor = {
          _id: transportPickup.transport_company_id,
          name: transportPickup.transport_company_name
        };
      }

      // Set pickup from search term and selected pickup from if available
      if (transportPickup.pickup_from) {
        this.pickupFromSearchTerm = transportPickup.pickup_from;
        this.selectedPickupFrom = {
          _id: transportPickup.pickup_from_id,
          name: transportPickup.pickup_from
        };
      }

      // Set delivered at search term and selected delivered at if available
      if (transportPickup.delivered_at) {
        this.deliveredAtSearchTerm = transportPickup.delivered_at;
        this.selectedDeliveredAt = {
          _id: transportPickup.delivered_at_id,
          name: transportPickup.delivered_at
        };
      }
    }
  }

  private populateVehicleFromPreSelected(vehicle: any): void {
    if (vehicle) {
      this.selectedVehicle = vehicle;
      this.vehicleSearchTerm = this.getVehicleDisplayName(vehicle);

      this.GetTransportPickupExists(vehicle._id);
      this.transportPickupForm.patchValue({
        vehicle_id: vehicle._id || vehicle.id,
        vehicle_name: this.vehicleSearchTerm
      });
    }
  }

  private resetForm(): void {
    this.transportPickupForm.reset();
    this.isLoading = false;
    this.isEditMode = false;
    this.isVehiclePreSelected = false;
    this.selectedTransportPickup = null;

    // Reset vehicle search data
    this.vehicleSearchTerm = '';
    this.selectedVehicle = null;
    this.vehicleSearchResults = [];
    this.showVehicleDropdown = false;
    this.isVehicleSearching = false;

    // Reset vendor search data
    this.vendorSearchTerm = '';
    this.selectedVendor = null;
    this.vendorSearchResults = [];
    this.showVendorDropdown = false;
    this.isVendorSearching = false;

    // Reset pickup from search data
    this.pickupFromSearchTerm = '';
    this.selectedPickupFrom = null;
    this.pickupFromSearchResults = [];
    this.showPickupFromDropdown = false;
    this.isPickupFromSearching = false;

    // Reset delivered at search data
    this.deliveredAtSearchTerm = '';
    this.selectedDeliveredAt = null;
    this.deliveredAtSearchResults = [];
    this.showDeliveredAtDropdown = false;
    this.isDeliveredAtSearching = false;
  }

  onSubmit(): void {
    if (this.transportPickupForm.valid) {
      this.isLoading = true;
      const formData = { ...this.transportPickupForm.value };

      if (this.isEditMode && this.selectedTransportPickup) {
        // Remove vehicle_name and vehicle_id from formData when in edit mode
        delete formData.vehicle_name;
        delete formData.vehicle_id;
        this.updateTransportPickup(formData);
      } else {
        this.addTransportPickup(formData);
      }
    } else {
      this.markFormGroupTouched(this.transportPickupForm);
      Swal.fire({
        title: 'Validation Error',
        text: 'Please fill in all required fields correctly.',
        icon: 'error',
        confirmButtonText: 'OK'
      });
    }
  }

  private addTransportPickup(formData: any): void {
    this.subscriptions.push(
      this._transportPickupService.AddData(formData).subscribe({
        next: (response: any) => {
          this.isLoading = false;
          if (response.status) {
            Swal.fire({
              title: 'Success!',
              text: 'Transport pickup has been added successfully.',
              icon: 'success',
              confirmButtonText: 'OK'
            }).then(() => {
              this.closeModal();
              this.transportPickupAdded.emit();
              this.resetForm();
            });
          } else {
            Swal.fire({
              title: 'Error!',
              text: response.message || 'Failed to add transport pickup.',
              icon: 'error',
              confirmButtonText: 'OK'
            });
          }
        },
        error: (error) => {
          this.isLoading = false;
          console.error('Error adding transport pickup:', error);
          Swal.fire({
            title: 'Error!',
            text: 'Failed to add transport pickup. Please try again.',
            icon: 'error',
            confirmButtonText: 'OK'
          });
        }
      })
    );
  }

  private updateTransportPickup(formData: any): void {
    const transportPickupId = this.selectedTransportPickup._id || this.selectedTransportPickup.id;
    this.subscriptions.push(
      this._transportPickupService.UpdateData(transportPickupId, formData).subscribe({
        next: (response: any) => {
          this.isLoading = false;
          if (response.status) {
            Swal.fire({
              title: 'Success!',
              text: 'Transport pickup has been updated successfully.',
              icon: 'success',
              confirmButtonText: 'OK'
            }).then(() => {
              this.closeModal();
              this.transportPickupUpdated.emit();
              this.resetForm();
            });
          } else {
            Swal.fire({
              title: 'Error!',
              text: response.message || 'Failed to update transport pickup.',
              icon: 'error',
              confirmButtonText: 'OK'
            });
          }
        },
        error: (error) => {
          this.isLoading = false;
          console.error('Error updating transport pickup:', error);
          Swal.fire({
            title: 'Error!',
            text: 'Failed to update transport pickup. Please try again.',
            icon: 'error',
            confirmButtonText: 'OK'
          });
        }
      })
    );
  }

  private closeModal(): void {
    const modalElement = document.getElementById('kt_modal_transport_pickup_add_edit');
    if (modalElement) {
      const modal = bootstrap.Modal.getInstance(modalElement);
      if (modal) {
        modal.hide();
      }
    }
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();
    });
  }

  // Form getters for validation
  get vehicle_id() { return this.transportPickupForm.get('vehicle_id'); }
  get vehicle_name() { return this.transportPickupForm.get('vehicle_name'); }
  get order_date() { return this.transportPickupForm.get('order_date'); }
  get yard_in() { return this.transportPickupForm.get('yard_in'); }
  get out_date() { return this.transportPickupForm.get('out_date'); }
  get yard_out() { return this.transportPickupForm.get('yard_out'); }
  get pos_no() { return this.transportPickupForm.get('pos_no'); }
  get pickup_from() { return this.transportPickupForm.get('pickup_from'); }
  get pickup_from_id() { return this.transportPickupForm.get('pickup_from_id'); }
  get delivered_at() { return this.transportPickupForm.get('delivered_at'); }
  get delivered_at_id() { return this.transportPickupForm.get('delivered_at_id'); }
  get transport_company_id() { return this.transportPickupForm.get('transport_company_id'); }
  get transport_company_name() { return this.transportPickupForm.get('transport_company_name'); }
  get comment_for_transporter() { return this.transportPickupForm.get('comment_for_transporter'); }
  get admin_comment() { return this.transportPickupForm.get('admin_comment'); }

  // Vehicle search methods
  private setupVehicleSearchDebounce(): void {
    this.vehicleSearchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(searchTerm => {
      this.searchVehicles(searchTerm);
    });
  }

  onVehicleSearchInput(event: Event): void {
    // Don't allow search if in edit mode or vehicle is pre-selected
    if (this.isEditMode || this.isVehiclePreSelected) {
      return;
    }

    const target = event.target as HTMLInputElement;
    this.vehicleSearchTerm = target.value;
    this.selectedVehicle = null;

    // Update the form control with the typed value
    this.transportPickupForm.patchValue({
      vehicle_name: this.vehicleSearchTerm,
      vehicle_id: ''
    });

    if (this.vehicleSearchTerm.length >= 2) {
      this.vehicleSearchSubject.next(this.vehicleSearchTerm);
      this.showVehicleDropdown = true;
    } else {
      this.showVehicleDropdown = false;
      this.vehicleSearchResults = [];
    }
  }

  onVehicleInputFocus(): void {
    // Don't show dropdown if in edit mode or vehicle is pre-selected
    if (this.isEditMode || this.isVehiclePreSelected) {
      return;
    }

    if (this.vehicleSearchResults.length > 0) {
      this.showVehicleDropdown = true;
    }
  }

  onVehicleInputBlur(): void {
    setTimeout(() => {
      this.showVehicleDropdown = false;
    }, 200);
  }

  private searchVehicles(searchTerm: string): void {
    // Don't search if in edit mode or vehicle is pre-selected
    if (this.isEditMode || this.isVehiclePreSelected) {
      return;
    }

    if (!searchTerm || searchTerm.length < 2) {
      this.vehicleSearchResults = [];
      this.showVehicleDropdown = false;
      return;
    }

    this.isVehicleSearching = true;
    this.subscriptions.push(
      this._vehicleService.GetData('1', searchTerm, { paginate: false, search: searchTerm }).subscribe({
        next: (response: any) => {
          this.isVehicleSearching = false;
          if (response.status && response.data) {
            // Handle both paginated and non-paginated responses
            this.vehicleSearchResults = response.data.data || response.data || [];
            this.showVehicleDropdown = this.vehicleSearchResults.length > 0;
          } else {
            this.vehicleSearchResults = [];
            this.showVehicleDropdown = false;
          }
        },
        error: (error: any) => {
          this.isVehicleSearching = false;
          console.error('Error searching vehicles:', error);
          this.vehicleSearchResults = [];
          this.showVehicleDropdown = false;
        }
      })
    );
  }

  private GetTransportPickupExists(vehicle_id: string): void {

    this.subscriptions.push(
      this._transportPickupService.GetData("1", '', { vehicle_id, paginate: false }).subscribe({
        next: (response: any) => {
          if (response.status && response.data) {
            let pickup = response.data.length ? response.data[0] : null;
            if (pickup) {
              this.populateForm(pickup);
              this.isEditMode = true;
              this.isVehiclePreSelected = false;
            }
          }
        }
      })
    )

  }

  selectVehicle(vehicle: any): void {
    this.selectedVehicle = vehicle;
    this.vehicleSearchTerm = this.getVehicleDisplayName(vehicle);

    this.transportPickupForm.patchValue({
      vehicle_id: vehicle._id || vehicle.id,
      vehicle_name: this.vehicleSearchTerm
    });

    this.showVehicleDropdown = false;
  }

  private getVehicleDisplayName(vehicle: any): string {
    const parts = [];
    if (vehicle.name) parts.push(vehicle.name);
    if (vehicle.company_name) parts.push(vehicle.company_name);
    if (vehicle.year) parts.push(`(${vehicle.year})`);
    return parts.join(' ') || 'Unknown Vehicle';
  }

  getVehicleImageUrl(vehicle: any): string {
    if (vehicle.pictures && vehicle.pictures.length > 0) {
      return vehicle.pictures[0];
    }
    return 'assets/media/svg/files/blank-image.svg'; // Default placeholder image
  }

  // Vendor search methods
  private setupVendorSearchDebounce(): void {
    this.vendorSearchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(searchTerm => {
      this.searchVendors(searchTerm);
    });
  }

  onVendorSearchInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.vendorSearchTerm = target.value;
    this.selectedVendor = null;

    // Update the form control with the typed value
    this.transportPickupForm.patchValue({
      transport_company_name: this.vendorSearchTerm,
      transport_company_id: ''
    });

    if (this.vendorSearchTerm.length >= 2) {
      this.vendorSearchSubject.next(this.vendorSearchTerm);
      this.showVendorDropdown = true;
    } else {
      this.showVendorDropdown = false;
      this.vendorSearchResults = [];
    }
  }

  onVendorInputFocus(): void {
    if (this.vendorSearchResults.length > 0) {
      this.showVendorDropdown = true;
    }
  }

  onVendorInputBlur(): void {
    setTimeout(() => {
      this.showVendorDropdown = false;
    }, 200);
  }

  private searchVendors(searchTerm: string): void {
    if (!searchTerm || searchTerm.length < 2) {
      this.vendorSearchResults = [];
      this.showVendorDropdown = false;
      return;
    }

    this.isVendorSearching = true;
    this.subscriptions.push(
      this._vendorService.GetData('1', searchTerm, { paginate: false, type: 'transport_company' }).subscribe({
        next: (response: any) => {
          this.isVendorSearching = false;
          if (response.status && response.data) {
            // Handle both paginated and non-paginated responses
            this.vendorSearchResults = response.data.data || response.data || [];
            this.showVendorDropdown = this.vendorSearchResults.length > 0;
          } else {
            this.vendorSearchResults = [];
            this.showVendorDropdown = false;
          }
        },
        error: (error: any) => {
          this.isVendorSearching = false;
          console.error('Error searching vendors:', error);
          this.vendorSearchResults = [];
          this.showVendorDropdown = false;
        }
      })
    );
  }

  selectVendor(vendor: any): void {
    this.selectedVendor = vendor;
    this.vendorSearchTerm = this.getVendorDisplayName(vendor);

    this.transportPickupForm.patchValue({
      transport_company_id: vendor._id || vendor.id,
      transport_company_name: this.vendorSearchTerm
    });

    this.showVendorDropdown = false;
  }

  private getVendorDisplayName(vendor: any): string {
    return vendor.company_name || 'Unknown Company';
  }

  // Pickup From search methods
  private setupPickupFromSearchDebounce(): void {
    this.pickupFromSearchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(searchTerm => {
      this.searchPickupFrom(searchTerm);
    });
  }

  onPickupFromSearchInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.pickupFromSearchTerm = target.value;
    this.selectedPickupFrom = null;

    // Update the form control with the typed value
    this.transportPickupForm.patchValue({
      pickup_from: this.pickupFromSearchTerm,
      pickup_from_id: ''
    });

    if (this.pickupFromSearchTerm.length >= 2) {
      this.pickupFromSearchSubject.next(this.pickupFromSearchTerm);
      this.showPickupFromDropdown = true;
    } else {
      this.showPickupFromDropdown = false;
      this.pickupFromSearchResults = [];
    }
  }

  onPickupFromInputFocus(): void {
    if (this.pickupFromSearchResults.length > 0) {
      this.showPickupFromDropdown = true;
    }
  }

  onPickupFromInputBlur(): void {
    setTimeout(() => {
      this.showPickupFromDropdown = false;
    }, 200);
  }

  private searchPickupFrom(searchTerm: string): void {
    if (!searchTerm || searchTerm.length < 2) {
      this.pickupFromSearchResults = [];
      this.showPickupFromDropdown = false;
      return;
    }

    this.isPickupFromSearching = true;
    this.subscriptions.push(
      this._auctionHallService.GetData('1', searchTerm, { paginate: false }).subscribe({
        next: (response: any) => {
          this.isPickupFromSearching = false;
          if (response.status && response.data) {
            // Handle both paginated and non-paginated responses
            this.pickupFromSearchResults = response.data.data || response.data || [];
            this.showPickupFromDropdown = this.pickupFromSearchResults.length > 0;
          } else {
            this.pickupFromSearchResults = [];
            this.showPickupFromDropdown = false;
          }
        },
        error: (error: any) => {
          this.isPickupFromSearching = false;
          console.error('Error searching pickup from:', error);
          this.pickupFromSearchResults = [];
          this.showPickupFromDropdown = false;
        }
      })
    );
  }

  selectPickupFrom(pickupFrom: any): void {
    this.selectedPickupFrom = pickupFrom;
    this.pickupFromSearchTerm = this.getPickupFromDisplayName(pickupFrom);

    this.transportPickupForm.patchValue({
      pickup_from: this.pickupFromSearchTerm,
      pickup_from_id: pickupFrom._id || pickupFrom.id
    });

    this.showPickupFromDropdown = false;
  }

  private getPickupFromDisplayName(pickupFrom: any): string {
    return pickupFrom.name || pickupFrom.company_name || 'Unknown Auction Hall';
  }

  // Delivered At search methods
  private setupDeliveredAtSearchDebounce(): void {
    this.deliveredAtSearchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(searchTerm => {
      this.searchDeliveredAt(searchTerm);
    });
  }

  onDeliveredAtSearchInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.deliveredAtSearchTerm = target.value;
    this.selectedDeliveredAt = null;

    // Update the form control with the typed value
    this.transportPickupForm.patchValue({
      delivered_at: this.deliveredAtSearchTerm,
      delivered_at_id: ''
    });

    if (this.deliveredAtSearchTerm.length >= 2) {
      this.deliveredAtSearchSubject.next(this.deliveredAtSearchTerm);
      this.showDeliveredAtDropdown = true;
    } else {
      this.showDeliveredAtDropdown = false;
      this.deliveredAtSearchResults = [];
    }
  }

  onDeliveredAtInputFocus(): void {
    if (this.deliveredAtSearchResults.length > 0) {
      this.showDeliveredAtDropdown = true;
    }
  }

  onDeliveredAtInputBlur(): void {
    setTimeout(() => {
      this.showDeliveredAtDropdown = false;
    }, 200);
  }

  private searchDeliveredAt(searchTerm: string): void {
    if (!searchTerm || searchTerm.length < 2) {
      this.deliveredAtSearchResults = [];
      this.showDeliveredAtDropdown = false;
      return;
    }

    this.isDeliveredAtSearching = true;
    this.subscriptions.push(
      this._vendorService.GetData('1', searchTerm, { paginate: false, type: 'yard' }).subscribe({
        next: (response: any) => {
          this.isDeliveredAtSearching = false;
          if (response.status && response.data) {
            // Handle both paginated and non-paginated responses
            this.deliveredAtSearchResults = response.data.data || response.data || [];
            this.showDeliveredAtDropdown = this.deliveredAtSearchResults.length > 0;
          } else {
            this.deliveredAtSearchResults = [];
            this.showDeliveredAtDropdown = false;
          }
        },
        error: (error: any) => {
          this.isDeliveredAtSearching = false;
          console.error('Error searching delivered at:', error);
          this.deliveredAtSearchResults = [];
          this.showDeliveredAtDropdown = false;
        }
      })
    );
  }

  selectDeliveredAt(deliveredAt: any): void {
    this.selectedDeliveredAt = deliveredAt;
    this.deliveredAtSearchTerm = this.getDeliveredAtDisplayName(deliveredAt);

    this.transportPickupForm.patchValue({
      delivered_at: this.deliveredAtSearchTerm,
      delivered_at_id: deliveredAt._id || deliveredAt.id
    });

    this.showDeliveredAtDropdown = false;
  }

  private getDeliveredAtDisplayName(deliveredAt: any): string {
    return deliveredAt.company_name || deliveredAt.delivered_at || 'Unknown Delivery Location';
  }
}
